import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faEdit,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import * as Sentry from "@sentry/react";
import Job from "../../apiResources/Job";
import Feedback from "../../apiResources/Feedback";
import LeaveReview from "./LeaveReview";
import ReviewLeft from "./ReviewLeft";
import { Column } from "./styles/job-styles";
import TextLink from "../library/TextLink";
import Button from "../library/Button";
import {
  DialogActions,
  DialogContent,
  Modal,
  ModalBackground,
  ModalHeading,
  ModalClose,
} from "../library/Modal";
import { FormError } from "../library/Form";
import { useModal, useBoolean } from "../../hooks";

const JobActionColumn = (props: Props) => {
  const { job, feedbacksForJob, onDelete } = props;

  const dialog = useModal();
  const [loading, setLoading] = useBoolean();
  const [errorMsg, setErrorMsg] = useState("");

  const handleLink = (link) => {
    window.location.assign(link);
  };

  if (job.status === "position_filled" || job.status === "expired") {
    const reviewsArePending = feedbacksForJob.length > 0;

    return (
      <Column flexSize={1} center review>
        {reviewsArePending ? <LeaveReview /> : <ReviewLeft />}
      </Column>
    );
  }

  if (job.publishedStatus === "draft") {
    const deleteJob = async () => {
      setLoading.on();

      try {
        const success = await job.destroy();

        if (success) {
          setLoading.off();
          onDelete(job.id);
        } else {
          setErrorMsg("Oops, something went wrong!");
        }
      } catch (error) {
        setLoading.off();
        setErrorMsg("Oops, something went wrong!");
        Sentry.captureException(error);
      }
    };

    return (
      <Column flexSize={2}>
        <Actions>
          <TextLink href={`/employer/jobs/${job.id}/edit`}>
            Edit job <FontAwesomeIcon icon={faEdit} />
          </TextLink>

          <TextLink onClick={dialog.open}>
            Delete job <FontAwesomeIcon icon={faTrash} />
          </TextLink>

          <Modal isOpen={dialog.isOpen} onDismiss={dialog.close}>
            <ModalBackground onClick={dialog.close} />
            <DialogContent>
              <ModalHeading>Delete Job</ModalHeading>
              {errorMsg && <FormError>{errorMsg}</FormError>}
              <p>
                Are you sure you want to delete the <strong>{job.title}</strong>{" "}
                job?
              </p>
              <DialogActions>
                <Button primary onClick={deleteJob} disabled={loading}>
                  {loading ? "Deleting job..." : "Confirm"}
                </Button>
                <Button tertiary onClick={dialog.close} name="Cancel" />
              </DialogActions>
              <ModalClose aria-label="Cancel" onClick={dialog.close} />
            </DialogContent>
          </Modal>
        </Actions>
      </Column>
    );
  }

  return (
    <Column
      flexSize={1}
      data-test-id="job-link"
      center
      arrow
      offerMade={job.status === "offer_made"}
      pinkArrow={job.hasNewApplications || job.hasNewEngagements}
      onClick={() => handleLink(job.jobHubJobProfilePath)}
    >
      <FontAwesomeIcon icon={faChevronRight} data-test-id="job-link" />
    </Column>
  );
};

type Props = {
  job: Job;
  feedbacksForJob: Feedback[];
  onDelete: (jobId: string) => void;
};

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;

  ${TextLink} {
    text-align: right;
    font-size: 13px;
  }

  > * {
    margin-bottom: 0.75rem;

    :last-child {
      margin-bottom: 0;
    }
  }
`;

export default JobActionColumn;
